import Image from 'next/image';
import { usePathname } from 'next/navigation';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useTitleContext } from '@app/PageTitleContext';

import { BranchMenusV2 } from '@api/generator';

import { useCounterTerminal } from '@hooks/useCounterTerminal';

import { RootState } from '@store';

import { CT_URL_LINK } from '@constants/screenConstants';
import { pageTitles } from '@constants/screenConstants';

import { SideMenu } from '../SideMenu';
import { UserMenu } from '../UserMenu/UserMenu';

import styles from './header.module.scss';

const languageNamespace = 'common';

interface HeaderProps {
  menuList: BranchMenusV2 | undefined;
  isMenuListLoading: boolean;
  fadcode: string;
  forceMenuOpen?: boolean; // Mainly just for storybook
  isFromCT?: boolean;
}

export const Header = ({
  menuList,
  isMenuListLoading,
  fadcode,
  forceMenuOpen,
  isFromCT,
}: HeaderProps) => {
  const { pageTitle, setPageTitle } = useTitleContext();
  const user = useSelector((state: RootState) => state.user);
  const path = usePathname();
  const { t } = useTranslation(languageNamespace);
  const { isElectron } = useCounterTerminal();

  useEffect(() => {
    setPageTitle(pageTitles[path]);
  }, [path, setPageTitle]);

  if (user.username === '') {
    return <div className={styles.errorDiv}>Login to see the header component</div>;
  }

  return (
    <header className={styles.mainHeader} data-testid="BboHeader">
      <div className={styles.sideMenuAndLogo}>
        {fadcode && (
          <SideMenu
            menuList={menuList}
            isMenuListLoading={isMenuListLoading}
            forceMenuOpen={forceMenuOpen}
          />
        )}
        <div className={styles.headerDivider} />
        <Image
          src="/post-office-logo-small.png"
          alt="Post Office Logo"
          width={59}
          height={44}
          quality={100}
        />
      </div>
      <div className={styles.headerDivider} />

      <div className={styles.pageTitle} data-dd-privacy="allow">
        {pageTitle}
      </div>
      {isFromCT && (
        <div>
          <a
            className={styles.ctLink}
            href={isElectron ? CT_URL_LINK : process.env.NEXT_PUBLIC_CT_SIMULATOR_LINK}>
            {t('counterTerminal')}
          </a>
        </div>
      )}
      <div className={styles.headerDivider} />
      <UserMenu />
    </header>
  );
};
